<template>
  <div class="report-share-modal">
    <portal v-if="!controlled" to="reportAction">
      <v-tooltip bottom :disabled="report.shareable">
        <template #activator="{ on }">
          <span v-on="on">
            <v-btn
              color="secondary"
              class="mr-2"
              :class="{
                'no-min-width': isMobile,
                'share-disabled': !report.shareable,
              }"
              :loading="resolving"
              @click="openShareModal"
            >
              <v-icon v-if="isMobile">share</v-icon>
              <template v-else>Share</template>
            </v-btn>
          </span>
        </template>
        <span>Reports of this type can’t be shared.</span>
      </v-tooltip>
    </portal>

    <v-dialog
      v-model="showModal"
      max-width="480px"
      content-class="fixed-height-sm"
      :retain-focus="false"
    >
      <modal-card title="Share Report" fixed @close="showModal = false">
        <div v-if="shareUrl" class="share-success pa-6 text-center">
          <v-icon color="success" size="48" class="mb-4">check_circle</v-icon>
          <h3 class="text-h6 mb-2">Link Ready to Share</h3>
          <p class="text--secondary mb-4">
            Anyone with this link can view the report
          </p>

          <div class="share-url-box">
            <v-text-field
              :value="shareUrl"
              readonly
              class="share-url-input no-label"
            />
            <copy-btn :copy="shareUrl" class="ml-2" icon />
          </div>
        </div>

        <div v-else class="pa-6 text-center text--secondary">
          No share link available for this report.
        </div>

        <template v-slot:actions>
          <v-btn
            v-if="shareUrl"
            color="appMain"
            :href="shareUrl"
            target="_blank"
          >
            <v-icon left>open_in_new</v-icon>
            Open
          </v-btn>
        </template>
      </modal-card>
    </v-dialog>
  </div>
</template>

<script>
import qs from 'qs';
import { mapGetters, mapActions } from 'vuex';
import CopyBtn from '@/components/basic/copy-btn/CopyBtn.vue';
import { appRoute } from '@/globals';

export default {
  name: 'ReportShareDialog',
  components: {
    CopyBtn,
  },
  props: {
    customReportId: {
      type: String,
      default: null,
    },
    report: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Boolean,
      default: undefined,
    },
  },

  data() {
    return {
      showModal: false,
      resolvedId: null,
      resolving: false,
    };
  },

  computed: {
    ...mapGetters('customReports', ['byId', 'allByClass']),

    customReport() {
      return this.byId(this.resolvedId);
    },

    controlled() {
      return this.value !== undefined;
    },

    shareUrl() {
      const pid = this.customReport?.pid;
      if (!pid) return '';
      const base = appRoute(`/reports/shared/${pid}`, true);
      const input = this.customReport?.input || {};
      const params = {};
      Object.entries(input).forEach(([param, settings]) => {
        if (settings.value !== null && settings.value !== undefined) {
          params[param] = settings.value;
        }
      });
      const pinnedFields = this.customReport?.pinned_fields;
      if (pinnedFields?.length) {
        params.pinned_fields = pinnedFields;
      }
      const query = qs.stringify(params);
      return query ? `${base}?${query}` : base;
    },
  },

  watch: {
    customReportId: {
      immediate: true,
      handler(val) {
        this.resolvedId = val;
      },
    },
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.openShareModal();
        } else {
          this.showModal = false;
        }
      },
    },
    showModal(val) {
      if (this.controlled && !val) {
        this.$emit('input', false);
      }
    },
  },

  methods: {
    ...mapActions('customReports', ['loadByClass', 'createOne']),

    async openShareModal() {
      if (!this.report.shareable) return;

      if (!this.resolvedId) {
        await this.resolveCustomReport();
      }
      this.showModal = true;
    },

    buildLockedInput() {
      const input = {};
      const controls = this.report?.controls || {};

      Object.values(controls).forEach((config) => {
        const param = config.param;
        if (!param) {
          return;
        }

        const value = this.filters[param];
        if (value === undefined || value === null || value === '') {
          return;
        }

        input[param] = {
          rule: config.shareable === false ? 'locked' : 'editable',
          value,
        };
      });

      return input;
    },

    async resolveCustomReport() {
      this.resolving = true;
      try {
        await this.loadByClass(this.report.id);
        const existing = this.allByClass(this.report.id)[0];
        if (existing) {
          this.resolvedId = existing.id;
          return;
        }
        const created = await this.createOne({
          title: this.report.title,
          class: this.report.id,
          secure: true,
          input: this.buildLockedInput(),
        });
        this.resolvedId = created?.id ?? null;
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style lang="less">
.share-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 280px;
  justify-content: center;
  height: 100%;
}

.share-url-box {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;

  .share-url-input {
    flex: 1;

    input {
      font-size: 13px;
    }
  }
}

.v-btn.share-disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}
</style>
